import React from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { GradientButton } from '@/components/ui/GradientButton';
import { 
  Lock, 
  Shield,
  Share2,
  Calendar,
  Sparkles,
  Zap,
  RefreshCw
} from 'lucide-react';

const TikTokLogin = () => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const { pathname } = useLocation();
  const referralCode = pathname.startsWith('/T-') ? pathname.slice(1) : null;

  const handleLogin = () => {
    let url = `${process.env.API_BASE_URL}/auth/tiktok`;
    if (referralCode) {
      url += `?rc=${referralCode}`
    }

    window.location.href = url;
  };

  return (
    <div className="h-screen w-screen overflow-hidden relative">
      <style>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animated-gradient {
          background: linear-gradient(
            -45deg,
            #1a0f2e,
            #2d1b4e,
            #432a75,
            #31204f,
            #1f1235
          );
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }

        .glow {
          position: absolute;
          width: 40%;
          height: 40%;
          filter: blur(100px);
          border-radius: 50%;
        }

        .glow-1 {
          top: -10%;
          left: -10%;
          background: rgba(123, 31, 162, 0.5);
          animation: move1 10s infinite alternate;
        }

        .glow-2 {
          bottom: -10%;
          right: -10%;
          background: rgba(103, 58, 183, 0.5);
          animation: move2 15s infinite alternate;
        }

        @keyframes move1 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(10%, 10%); }
        }

        @keyframes move2 {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-10%, -10%); }
        }
      `}</style>

      <div className="animated-gradient absolute inset-0">
        <div className="glow glow-1" />
        <div className="glow glow-2" />
      </div>

      <div className="relative h-full w-full">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full" />
        <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 blur-3xl opacity-20 w-96 h-96 bg-gradient-to-tr from-pink-500 to-purple-500 rounded-full" />
        
        {/* Content */}
        <div className="relative h-full w-full flex flex-col items-center justify-center p-4">
          <div className="w-full max-w-md text-center space-y-8">
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-xl backdrop-blur-xl text-red-200 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <Shield className="h-4 w-4" />
                  {error}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Badge className="bg-gradient-to-r from-indigo-500 to-pink-500 mb-4 rounded-full animate-bounce">
                <span className="flex items-center gap-1">
                  <span>🚀</span> Creator Growth Platform <span>✨</span>
                </span>
              </Badge>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 text-transparent bg-clip-text">
                Amplify Your Influence
              </h1>
              <p className="text-gray-300 text-lg">
                Turn your content into a cross-platform empire
              </p>
            </div>

            <Card className="p-8 bg-white/[0.03] backdrop-blur-xl border-white/[0.05] hover:border-purple-500/50 transition-all duration-300 rounded-2xl">
              <div className="space-y-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { emoji: '🌟', title: 'Multi-Platform', desc: 'Reach wider audiences' },
                    { emoji: '⏰', title: 'Smart Planning', desc: 'Perfect timing' },
                    { emoji: '🚀', title: 'Growth Tools', desc: 'Maximize engagement' },
                    { emoji: '🔄', title: 'Content Flow', desc: 'Seamless distribution' }
                  ].map((feature, i) => (
                    <div key={i} className="text-center p-4 bg-white/5 backdrop-blur-sm rounded-xl hover:bg-white/[0.08] transition-all">
                      <div className="text-3xl mb-2">{feature.emoji}</div>
                      <p className="font-medium text-[15px] text-white">{feature.title}</p>
                      <p className="text-xs text-gray-300 font-normal">{feature.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <GradientButton 
                    onClick={handleLogin}
                    className="w-full py-3 text-[15px] font-medium shadow-xl hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-0.5 rounded-xl"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg 
                        viewBox="0 0 24 24" 
                        className="h-5 w-5"
                        fill="currentColor"
                      >
                        <path d="M19.59 6.69a4.83 4.83 0 0-1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0-1-5.2 1.74 2.89 2.89 0 0-1 2.31-4.64 2.93 2.93 0 0-1 .88.13V9.4a6.84 6.84 0 0-0-1-.05A6.33 6.33 0 0-0 5 20.1a6.34 6.34 0 0-0 10.86-4.43v-7a8.16 8.16 0 0-0 4.77 1.52v-3.4a4.85 4.85 0 0-1-1-.1z"/>
                      </svg>
                      Start with TikTok
                      <span className="group-hover:translate-x-1 transition-transform">🎯</span>
                    </span>
                  </GradientButton>
                  <p className="text-xs text-center text-gray-400">
                    Join creators building their cross-platform empire
                  </p>
                </div>
              </div>
            </Card>

            <p className="text-xs text-gray-400 flex items-center justify-center gap-2">
              <Lock className="h-3 w-3" />
              Enterprise-grade security & reliability
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TikTokLogin;